// src/pages/ArticlesLibrary.tsx
import React, { useState, useEffect } from 'react';
import ArticleModal from '../components/ArticleModal';
import DonationSidebar from '../components/DonationSidebar';

interface Article {
  id: string;
  title: string;
  meta: string;
  excerpt?: string;
  category?: string;
  readTime?: string;
}

const ArticlesLibrary: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [activeArticle, setActiveArticle] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const articleList: Article[] = [
        {
          id: "importance-of-prayer",
          title: "The Importance of Prayer in Islam: Your Direct Connection with Allah",
          meta: "7 min read • Spiritual Growth",
          excerpt: "Discover why prayer is the central pillar of Islam and how it transforms your spiritual life, mental health, and daily discipline.",
          category: "Spiritual Growth",
          readTime: "7 min read"
        },
        {
          id: "power-of-gratitude",
          title: "The Power of Gratitude in Islam: How Shukr Transforms Your Life",
          meta: "6 min read • Spiritual Growth",
          excerpt: "Learn how practicing gratitude (Shukr) can increase your blessings, improve mental health, and strengthen your relationship with Allah.",
          category: "Spiritual Growth",
          readTime: "6 min read"
        },
        {
          id: "understanding-patience",
          title: "Understanding Patience in Islam: The Key to Spiritual Strength",
          meta: "8 min read • Personal Development",
          excerpt: "Explore the different types of patience (Sabr) in Islam and practical ways to develop this essential virtue in modern life.",
          category: "Personal Development",
          readTime: "8 min read"
        },
        {
          id: "beauty-of-morning-dhikr",
          title: "The Beauty of Morning Dhikr: Starting Your Day with Allah's Remembrance",
          meta: "5 min read • Daily Practice",
          excerpt: "Discover the powerful morning adhkar and how they can protect you, bring barakah to your day, and strengthen your connection with Allah.",
          category: "Daily Practice",
          readTime: "5 min read"
        },
        {
          id: "story-of-prophet-yusuf",
          title: "The Story of Prophet Yusuf: Lessons in Patience, Trust and Divine Plan",
          meta: "10 min read • Prophet Stories",
          excerpt: "Explore the profound lessons from the life of Prophet Yusuf (AS) - from betrayal to leadership, and the beautiful wisdom in Allah's plan.",
          category: "Prophet Stories",
          readTime: "10 min read"
        },
        {
          id: "quranic-guidance-for-anxiety",
          title: "Quranic Guidance for Anxiety: Finding Peace in Modern Challenges",
          meta: "6 min read • Emotional Healing",
          excerpt: "Learn how the Quran provides practical solutions for anxiety, worry, and stress through powerful verses and timeless wisdom.",
          category: "Emotional Healing",
          readTime: "6 min read"
        }
      ];
      setArticles(articleList);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenArticle = (articleId: string) => {
    setActiveArticle(articleId);
  };

  if (loading) {
    return (
      <div className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📚</div>
            <p>Loading Articles...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section-padding">
      <div className="container">
        {/* Back Button */}
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => window.history.back()}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--accent-primary)',
              cursor: 'pointer',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            ← Back
          </button>
        </div>

        <div className="section-title">
          <h1>Islamic Articles Library</h1>
          <p>Deep dive into comprehensive Islamic knowledge and spiritual guidance</p>
        </div>

        {/* Articles Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
          gap: '32px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {articles.map((article) => (
            <div key={article.id} className="card" style={{ cursor: 'pointer' }} onClick={() => handleOpenArticle(article.id)}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '20px',
                gap: '12px'
              }}>
                <span style={{
                  backgroundColor: 'var(--accent-primary)',
                  color: 'var(--bg-primary)',
                  padding: '6px 16px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '500'
                }}>
                  {article.category}
                </span>
                <span style={{ 
                  color: 'var(--text-secondary)', 
                  fontSize: '14px' 
                }}>
                  {article.readTime}
                </span>
              </div>
              <h3 style={{
                fontSize: '1.375rem',
                fontWeight: '600',
                marginBottom: '16px',
                lineHeight: '1.4',
                color: 'var(--text-primary)'
              }}>
                {article.title}
              </h3>
              <p style={{
                color: 'var(--text-secondary)',
                marginBottom: '24px',
                lineHeight: '1.7'
              }}>
                {article.excerpt}
              </p>
              <button 
                className="btn-secondary" 
                style={{ width: '100%' }}
              >
                Read Full Article
              </button>
            </div>
          ))}
        </div>

        {/* Coming Soon Notice */}
        <div style={{
          textAlign: 'center',
          marginTop: '60px',
          padding: '40px',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '12px',
          border: '1px solid var(--border)'
        }}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>
            More Articles Coming Soon
          </h3>
          <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
            I am continuously adding new articles on various Islamic topics including Quranic studies, 
            Prophet stories, Islamic history, and practical guidance for modern life.
          </p>
        </div>

        {/* Article Modal */}
        <ArticleModal 
          id={activeArticle} 
          onClose={() => setActiveArticle(null)} 
        />

        {/* Donation Sidebar */}
        <DonationSidebar />
      </div>
    </div>
  );
};

export default ArticlesLibrary;